import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent,
} from '@wcg/frontend-framework';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SingleTextDialogData } from '../../../types';
import { DIALOG_MODES } from '@erm/cluster';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    WcgDialogComponent,
    ReactiveFormsModule,
    WcgButtonComponent,
    WcgInputFieldComponent,
  ],
  selector: 'wcg-single-text-dialog',
  styleUrls: ['./single-text.dialog.component.scss'],
  template: `
    @defer (when singleTextForm && data) {
      <form [formGroup]="singleTextForm">
        <wcg-dialog
          [title]="data.title"
          [description]="data.description"
          [size]="data.size"
          data-testid="single-text-dialog"
        >
          <div class="w-full flex mt-8 single-text-dialog__content" content>
            @if (data.mode !== 'delete') {
              <wcg-input-field
                class="w-full"
                [errorText]="data.errorText!"
                formControlName="text"
                [isRequired]="data?.isRequired ?? false"
                [label]="data.label"
                data-testid="single-text-dialog__input-field"
              />
            }
          </div>
          <div tertiary>
            <wcg-button
              color="tertiary"
              data-testid="single-text-dialog__cancel-button"
              (click)="close()"
            >
              Cancel
            </wcg-button>
          </div>
          <div primary>
            @let canDelete = data.mode === 'delete';
            @let textLabel = canDelete ? 'Remove' : 'Save';
            @let dataTestId =
              canDelete
                ? 'single-text-dialog__remove-button'
                : 'single-text-dialog__save-button';
            <wcg-button
              [color]="canDelete ? 'primary-warn' : 'primary'"
              [attr.data-testid]="dataTestId"
              [isDisabled]="!canDelete && singleTextForm.invalid"
              (click)="save()"
            >
              {{ textLabel }}
            </wcg-button>
          </div>
        </wcg-dialog>
      </form>
    }
  `,
})
export class WcgSingleTextDialogComponent implements OnInit {
  private readonly _dialogRef = inject(
    MatDialogRef<WcgSingleTextDialogComponent>,
  );
  public readonly data = inject<SingleTextDialogData<string>>(MAT_DIALOG_DATA);
  private readonly _formBuilder = inject(FormBuilder);

  singleTextForm = this._createForm();
  editMode = new Set(['edit', 'editName']);

  ngOnInit(): void {
    this._setTextValidators();
    if (this.editMode.has(this.data.mode)) {
      this.singleTextForm.patchValue({
        text: this.data.data as string,
      });
    }
  }

  close(): void {
    this._dialogRef.close();
  }

  save(): void {
    const value = this.singleTextForm.value.text;

    switch (this.data.mode) {
      case DIALOG_MODES.CREATE:
        if (value) {
          this._dialogRef.close({
            value,
            propKey: this.data.propKey,
            mode: this.data.mode,
          });
        }
        break;

      case DIALOG_MODES.EDITNAME:
        this._dialogRef.close({
          value,
          propKey: this.data.propKey,
          mode: this.data.mode,
        });
        break;

      case DIALOG_MODES.EDIT:
      case DIALOG_MODES.DELETE:
        this._dialogRef.close({
          value,
          propKey: this.data.propKey,
          index: this.data.index,
          mode: this.data.mode,
        });
        break;

      default:
        console.warn('Unhandled mode:', this.data.mode);
        break;
    }
  }

  private _createForm() {
    return this._formBuilder.group({
      text: new FormControl<string | null>('', { nonNullable: false }),
    });
  }

  private _setTextValidators(): void {
    if (this.data) {
      if (typeof this.data.validatorFn === 'function') {
        this.singleTextForm.controls.text.setValidators([
          this.data.validatorFn(),
        ]);
      } else {
        this.singleTextForm.controls.text.clearValidators();
      }
      this.singleTextForm.controls.text.updateValueAndValidity();
    }
  }
}
